import { AlertBS, ButtonBS, Flex, Grid, Heading, Stack } from "@biblioteksentralen/react";
import {
  eventDateString,
  eventIsCancelled,
  eventIsFinished,
  eventIsOpen,
  eventTimeString,
  getEventHeadings,
  isRepeatedEventOccurence,
  ResolvedEvent,
} from "@libry-content/common";
import { dateFnsLocaleMap } from "@libry-content/localization";
import format from "date-fns/format";
import { Calendar } from "react-feather";
import ItemGrid from "../../searchAndCollection/components/works/ItemGrid";
import { logClick } from "../../utils/analytics/Plausible";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { ArticleHeader } from "../ArticleHeader";
import { EventBadges } from "../Badge";
import { DefaultContainer } from "../ContentContainer";
import BlockContent from "../blockContent/BlockContent";
import { useRelevantPublicationsHeading } from "../lists/utils";
import { ServicePreview } from "../services/ServicePreview";
import EventImageWithStatusBadge from "./EventImageWithStatusBadge";
import { EventLocation } from "./EventLocation";
import FutureOccurrencesList from "./FutureOccurrencesList";
import { Meta } from "./Meta";
import RegistrationButton from "./RegistrationButton";
import RegistrationStatus from "./RegistrationStatus";
import { getEventScheduleDetails } from "./utils";
import { ContactCard } from "../employees/ContactCard";

interface Props {
  event?: ResolvedEvent;
}

export const EventView = ({ event }: Props) => {
  const { t, ts, lang } = useTranslation();
  const relevantPublicationsHeading = useRelevantPublicationsHeading(event?.relevantPublications);

  if (!event) return null;

  const { heading, subheading } = getEventHeadings(event);

  return (
    <DefaultContainer>
      <Stack gap="2rem">
        <ArticleHeader
          title={ts(heading)}
          subtitle={ts(subheading)}
          teaser={ts(event.teaser)}
          badges={<EventBadges event={event} />}
          cusommImage={<EventImageWithStatusBadge event={event} />}
          document={{ _id: isRepeatedEventOccurence(event) ? event.series.id : event._id, _type: "event" }}
        />
        {eventIsOpen(event.eventStatus) && <RegistrationStatus registration={event.registration} />}
        {eventIsFinished(event) && !eventIsCancelled(event.eventStatus) && (
          <AlertBS status="warning" marginTop={{ base: "2rem", md: "0" }}>
            {t("Dette arrangementet er avholdt")}
          </AlertBS>
        )}
        {!eventIsOpen(event.eventStatus) && (
          <AlertBS status="warning">
            {eventIsCancelled(event.eventStatus)
              ? t("Dette arrangementet er avlyst")
              : t("Dette arrangementet er fullt")}
          </AlertBS>
        )}
        <Flex as="section" alignItems="baseline" direction={{ base: "column", md: "row" }} gap="2rem 4rem">
          <Stack gap="2rem">
            <Stack gap=".5rem">
              <DateAndTime event={event} />
              <Meta label={t("Sted")}>
                <EventLocation event={event} />
              </Meta>
              {ts(event.organizer) && <Meta label={t("Arrangør")}>{ts(event.organizer)}</Meta>}
            </Stack>
            <Stack>
              {eventIsOpen(event.eventStatus) && (
                <RegistrationButton
                  registration={event.registration}
                  registrationUrlOrEmailOrPhone={event.registrationUrlOrEmailOrPhone}
                  digitalTicket={event.digitalTicket}
                  ticketInfo={event.ticketInfo}
                />
              )}
              {!eventIsFinished(event) && (
                <ButtonBS
                  as="a"
                  variant="secondary"
                  href={`/api/internal/${lang}/ical?idOrKey=${event._id}`}
                  onClick={() => logClick("Arrangement:Ical")}
                >
                  {t("Legg til i kalender")}
                  <Calendar size="1em" />
                </ButtonBS>
              )}
            </Stack>
            <FutureOccurrencesList event={event} />
          </Stack>
          <BlockContent blocks={ts(event.body)} maxW="100%" />
        </Flex>
        <Stack gap="3rem">
          {(event.relevantPublications?.length ?? 0) > 0 && (
            <Stack>
              <Heading as="h2" fontSize="xl">
                {relevantPublicationsHeading}
              </Heading>
              <ItemGrid items={event.relevantPublications} />
            </Stack>
          )}
          {event.contacts && (
            <Stack gap="0.75rem">
              <Heading as="h2" fontSize="xl">
                {t("Kontakt")}
              </Heading>
              <Grid gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="2rem">
                {event.contacts.map((contact) => (
                  <ContactCard key={contact._id} contact={contact} hidePositionAndAffiliations />
                ))}
              </Grid>
            </Stack>
          )}
          {event.relatedService?.summary && (
            <Stack gap="0.75rem">
              <Heading as="h2" fontSize="xl">
                {t("Les mer om")}
              </Heading>
              <ServicePreview service={event.relatedService.summary} isFullsize />
            </Stack>
          )}
        </Stack>
      </Stack>
    </DefaultContainer>
  );
};

const DateAndTime = (props: { event: ResolvedEvent }) => {
  const { t, lang } = useTranslation();
  const { isMultiDayEvent, allDay, startsAt, endsAt } = getEventScheduleDetails(props.event, lang);

  if (!startsAt || !endsAt) return null;

  if (allDay) {
    return (
      <>
        <Meta label={t("Dato")}>{eventDateString(lang, props.event)}</Meta>
        <Meta label={t("Tid")}>{t("Hele dagen")}</Meta>
      </>
    );
  }

  if (isMultiDayEvent) {
    return (
      <Meta label={t("Tidspunkt")}>
        {format(new Date(startsAt), "d. MMM p", { locale: dateFnsLocaleMap[lang] })} -{" "}
        {format(new Date(endsAt), "d. MMM p", { locale: dateFnsLocaleMap[lang] })}
      </Meta>
    );
  }

  return (
    <>
      <Meta label={t("Dato")}>{eventDateString(lang, props.event)}</Meta>
      <Meta label={t("Tid")}>{eventTimeString(lang, props.event)}</Meta>
    </>
  );
};
